'use client'

import { memo, useCallback, useEffect, useState, type FC } from 'react'
import { useWatch } from 'react-hook-form'

import { ChecklistOptionRow } from './ChecklistOptionRow'
import { FieldShell, type FieldRendererProps } from './FieldShell'
import {
  AMOUNT_PARAM_KEY,
  buildRow,
  getAvailableBalance,
  getCurrency,
  parseAmount,
  sumCheckedRows,
  toEntries,
  type ChecklistField,
  type ChecklistRow,
} from '../lib/checklistHelper'
import { cn } from '@fp/ui-kit'
import { formatCurrency } from '@lib/formatCurrency'

const EMPTY_TEXT = 'No options available.'
type ChecklistTotalsProps = { total: number; balance?: number; currency: string }

const ChecklistTotals: FC<ChecklistTotalsProps> = memo(({ total, balance, currency }) => {
  const isOverBalance = typeof balance !== 'undefined' && total > balance

  return (
    <div className='grid gap-1 border-t border-muted pt-3'>
      <div className='flex items-center justify-between gap-2'>
        <span className='text-sm'>Total</span>
        <span
          className={cn(
            'text-lg font-medium tabular-nums',
            isOverBalance ? 'text-destructive-foreground' : 'text-foreground',
          )}
        >
          {formatCurrency(total, currency)}
        </span>
      </div>
      {typeof balance !== 'undefined' && (
        <div className='flex items-center justify-between gap-2 text-xs text-muted-foreground'>
          <span>Available balance</span>
          <span className='tabular-nums'>{formatCurrency(balance, currency)}</span>
        </div>
      )}
    </div>
  )
})
ChecklistTotals.displayName = 'ChecklistTotals'

export type EditableChecklistFieldProps = Omit<FieldRendererProps, 'paramsField'> & { paramsField: ChecklistField }

export const EditableChecklistField: FC<EditableChecklistFieldProps> = memo(
  ({ paramsField, field, error, helperText }) => {
    const { options } = paramsField
    const { onChange } = field
    const [rows, setRows] = useState<Array<ChecklistRow>>(() => options.map(buildRow))
    const externalAmount = parseAmount(useWatch({ name: AMOUNT_PARAM_KEY }))

    useEffect(() => {
      setRows(options.map(buildRow))
    }, [options])

    const emit = useCallback(
      (next: Array<ChecklistRow>) => {
        setRows(next)
        onChange(toEntries(next))
      },
      [onChange],
    )

    const toggleRow = useCallback(
      (index: number, checked: boolean) => emit(rows.map((row, i) => (i === index ? { ...row, checked } : row))),
      [emit, rows],
    )

    const changeValue = useCallback(
      (index: number, paramKey: string, value: string) =>
        emit(rows.map((row, i) => (i === index ? { ...row, values: { ...row.values, [paramKey]: value } } : row))),
      [emit, rows],
    )

    return (
      <FieldShell paramsField={paramsField} error={error} helperText={helperText}>
        {options.length ? (
          <div className='divide-y divide-muted rounded-md border border-muted'>
            {options.map((option, index) => {
              const row = rows[index]

              if (!row) return null

              const rowId = `dynamic-field-${paramsField.param_key}-${index}`

              return (
                <ChecklistOptionRow
                  key={rowId}
                  option={option}
                  row={row}
                  rowId={rowId}
                  index={index}
                  onToggle={toggleRow}
                  onValueChange={changeValue}
                />
              )
            })}
          </div>
        ) : (
          <p className='text-sm text-muted-foreground'>{EMPTY_TEXT}</p>
        )}

        <ChecklistTotals
          total={sumCheckedRows(options, rows) + externalAmount}
          balance={getAvailableBalance(paramsField)}
          currency={getCurrency(paramsField)}
        />
      </FieldShell>
    )
  },
)
EditableChecklistField.displayName = 'EditableChecklistField'
